<template>
  <Modal
    v-if="showShareMethodChoice"
    :active="active"
    :scrolling="true"
    class="medium_large"
    :class="{ 'classic-theme-light-only': !inheritClassicTheme }"
    @close="emits('close')"
  >
    <template #header>
      {{ $t("customFlows.shareModal.title.selectMethod") }}
    </template>

    <template #body>
      <div class="share_method_options">
        <button
          class="button method"
          :class="{ disabled: !directFlowShareAvailable }"
          :disabled="!directFlowShareAvailable"
          @click="startFlowShare"
        >
          <span class="material-symbols-outlined">dynamic_feed</span>
          <span>{{ $t("customFlows.shareModal.method.flow") }}</span>
        </button>

        <button class="button method" @click="startDocumentShare">
          <span class="material-symbols-outlined">description</span>
          <span>{{ $t("customFlows.shareModal.method.documents") }}</span>
        </button>

        <div v-if="directPreviewFetching" class="info">
          {{ $t("sharedUi.common.loading") }}
        </div>
        <div v-else-if="directShareBlockedMessage" class="info">
          {{ directShareBlockedMessage }}
        </div>
      </div>
    </template>

    <template #footer>
      <div class="buttons">
        <button class="button" @click="emitsClose">
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </Modal>

  <DocumentSpacePicker
    :inherit-classic-theme="inheritClassicTheme"
    v-else-if="shareFlowItemMode"
    :flowId="flowId"
    @submit="shareFlowItems"
    @close="emitsClose"
  />

  <Modal
    v-else
    :active="active"
    :scrolling="true"
    class="medium_large"
    :class="{ 'classic-theme-light-only': !inheritClassicTheme }"
    @close="emits('close')"
  >
    <template #header>
      {{
        flowState === FlowState.SELECT_SPACE
          ? $t("customFlows.shareModal.title.selectSpace")
          : $t("customFlows.shareModal.title.selectDocuments")
      }}
    </template>

    <template #body>
      <DocumentSpacePicker
        :inherit-classic-theme="inheritClassicTheme"
        v-if="flowState === FlowState.SELECT_SPACE"
        @submit="selectSpaces"
        @close="emitsClose"
      />
      <ShareFlowDocuments
        v-else-if="flowState === FlowState.SHARE_DOCUMENTS"
        :flowId="flowId"
        @change="updateDocumentIds"
      />
    </template>

    <template #footer>
      <div class="buttons">
        <button class="button" @click="emitsClose">
          {{ $t("cancel") }}
        </button>

        <button class="button" @click="goBack">
          {{ $t("back") }}
        </button>

        <button
          v-if="selectedSpaceIds.length > 0"
          class="button primary"
          :disabled="selectedDocumentIds.length === 0"
          @click="shareFlowDocuments"
        >
          {{ $t("customFlows.shareModal.submit") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { DocumentSpacePicker, Modal } from "@/components";
import ShareFlowDocuments from "@/views/custom_flows/share_flow_modal/ShareFlowDocuments.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useCreateSpaceItemsMutation } from "@/graphql/operations";
import { useStore } from "vuex";
import {
  describeFlowShareViolation,
  firstGlobalBlockingFlowShareViolation,
  firstBlockingFlowShareViolation,
  flowShareExistingSpaceNames,
  flowShareSucceeded,
  previewFlowShareToSpaces,
  useFlowSpaceShareMutation,
} from "@/composables/useFlowSpaceShare";
import { useI18n } from "vue-i18n";

const emits = defineEmits(["close", "success"]);
const emitsClose = () => emits("close");

const resultState = ref<string>("");
const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);
const { t } = useI18n();

const props = defineProps({
  inheritClassicTheme: { type: Boolean, default: false },
  active: {
    type: Boolean,
    required: true,
  },
  flowId: {
    type: String,
    required: true,
  },
});

watch(
  canUseSharedSpaces,
  (enabled) => {
    if (!enabled) emitsClose();
  },
  { immediate: true },
);

const shareAsFlowItem = computed(
  () => store.getters.user?.canShareCustomFlowsInSharedSpaces?.() ?? false,
);

type ShareMode = "flow" | "documents";
const shareMode = ref<ShareMode | null>(null);

const showShareMethodChoice = computed(
  () => shareAsFlowItem.value && shareMode.value === null,
);
const shareFlowItemMode = computed(
  () => shareAsFlowItem.value && shareMode.value === "flow",
);

enum FlowState {
  SELECT_SPACE,
  SHARE_DOCUMENTS,
}

const flowState = ref<FlowState>(FlowState.SELECT_SPACE);

const directPreviewFetching = ref(false);
const directPreviewLoaded = ref(false);
const directPreviewError = ref(false);
const directPreviewResult =
  ref<Awaited<ReturnType<typeof previewFlowShareToSpaces>>>(null);

onMounted(async () => {
  if (!shareAsFlowItem.value) {
    shareMode.value = "documents";
    return;
  }

  await loadDirectPreview();
});

async function loadDirectPreview() {
  directPreviewFetching.value = true;
  directPreviewError.value = false;
  try {
    directPreviewResult.value = await previewFlowShareToSpaces(
      props.flowId,
      null,
    );
  } catch {
    directPreviewError.value = true;
  } finally {
    directPreviewLoaded.value = true;
    directPreviewFetching.value = false;
  }
}

const directBlockingViolation = computed(() =>
  firstGlobalBlockingFlowShareViolation(directPreviewResult.value),
);

const directShareBlockedMessage = computed(() => {
  if (directPreviewError.value) {
    return t("customFlows.shareModal.previewError");
  }

  if (directBlockingViolation.value) {
    return t("customFlows.shareModal.flowBlocked", {
      reason: describeFlowShareViolation(
        (key, params) => t(key, params),
        directBlockingViolation.value,
      ),
    });
  }

  return "";
});

const directFlowShareAvailable = computed(
  () =>
    directPreviewLoaded.value &&
    !directPreviewFetching.value &&
    !directPreviewError.value &&
    !directBlockingViolation.value,
);

function startFlowShare() {
  if (!directFlowShareAvailable.value) return;
  shareMode.value = "flow";
}

function startDocumentShare() {
  shareMode.value = "documents";
  flowState.value = FlowState.SELECT_SPACE;
  selectedSpaceIds.value = [];
  selectedDocumentIds.value = [];
}

const selectedSpaceIds = ref<Array<string>>([]);
const selectSpaces = (spaceIds: Array<string>) => {
  selectedSpaceIds.value = spaceIds;
  flowState.value = FlowState.SHARE_DOCUMENTS;
};

const goBack = () => {
  if (flowState.value === FlowState.SHARE_DOCUMENTS) {
    selectedSpaceIds.value = [];
    selectedDocumentIds.value = [];
    flowState.value = FlowState.SELECT_SPACE;
    return;
  }

  if (shareAsFlowItem.value) {
    shareMode.value = null;
  }
};

const selectedDocumentIds = ref<string[]>([]);
const updateDocumentIds = (docIds: string[]) => {
  selectedDocumentIds.value = docIds;
};

const { executeMutation: createDocumentSpaceItem } =
  useCreateSpaceItemsMutation();

const { shareFlowToSpaces } = useFlowSpaceShareMutation();

const flowShareErrorMessage = (
  result: Awaited<ReturnType<typeof shareFlowToSpaces>>,
) => {
  const existingSpaceNames = flowShareExistingSpaceNames(result);

  if (existingSpaceNames.length > 0) {
    return t("customFlows.shareModal.alreadySharedInSpaces", {
      spaces: existingSpaceNames.join(", "),
    });
  }

  return t("customFlows.shareModal.flowBlocked", {
    reason: describeFlowShareViolation(
      (key, params) => t(key, params),
      firstBlockingFlowShareViolation(result),
    ),
  });
};

const shareFlowItems = async (spaceIds: Array<string>) => {
  selectedSpaceIds.value = spaceIds;

  try {
    const targets = spaceIds.map((id: string) => ({ spaceId: id }));
    const result = await shareFlowToSpaces(props.flowId, targets);

    if (!flowShareSucceeded(result)) {
      resultState.value = "error";
      emits(
        "success",
        resultState.value,
        selectedSpaceIds.value,
        flowShareErrorMessage(result),
      );
      return;
    }

    resultState.value = "success";
    emits("success", resultState.value, selectedSpaceIds.value);
  } catch {
    resultState.value = "error";
    emits("success", resultState.value, selectedSpaceIds.value);
  }
};

const shareFlowDocuments = async () => {
  let success = true;
  try {
    // for now execute mutations sequentially instead of parallel
    const targets = selectedSpaceIds.value.map((id: string) => ({
      spaceId: id,
    }));
    for (const docId of selectedDocumentIds.value) {
      const res = await createDocumentSpaceItem({
        targets: targets,
        documentId: docId,
      });
      console.log(res);
    }
  } catch {
    success = false;
  }

  resultState.value = success ? "success" : "error";
  emits("success", resultState.value, selectedSpaceIds.value);
};
</script>

<style scoped>
.share_method_options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.button.method {
  justify-content: flex-start;
  padding: 1rem;
  width: 100%;
}

.button.method .material-symbols-outlined {
  margin-right: 0.5rem;
}

.info {
  color: var(--classic-text-secondary, #555555);
  font-style: italic;
  text-align: center;
}
</style>
